/**
 * PR-E5 — SHX text run → flat line-segment vertex buffer.
 *
 * Counterpart to `textRunRenderer.renderTextRun` (which builds
 * instanced-glyph quads for SDF atlases). For SHX fonts the renderer
 * walks each glyph's pre-flattened stroke list (see `shxStrokeAtlas.ts`)
 * and emits a Float32 vertex pair buffer ready for a `LineSegments2`
 * mesh (one segment = 2 vertices = 6 floats).
 *
 * # Coordinate model
 *
 * SHX glyphs are stored in font units with origin at (0,0) and +Y up
 * (matches AutoCAD convention). The renderer converts to text-local
 * world units via:
 *
 *   sx = heightWorld / above × widthFactor
 *   sy = heightWorld / above
 *
 * where `above` is the SHX header's baseline → cap-top distance. This
 * matches `renderShxText` in the legacy SVG path so SHX text in the
 * Three.js renderer reads the same visual height as the SVG fallback.
 *
 * # Oblique shear
 *
 * `\Q` oblique angle is applied per vertex inside the renderer (NOT as
 * a mesh-level shear matrix) because Three.js's `LineSegments2`
 * doesn't have an instanced-shear path and a per-mesh shear would
 * complicate cursor advance. The shear formula:
 *
 *   x' = x + y × tan(obliqueRad)
 *
 * is applied AFTER the height + width-factor scaling so the shear is
 * geometrically equivalent to AutoCAD's TEXTOBJ obliquing. This
 * mirrors `glyphToSvgPath` in the SVG path so visual parity holds.
 *
 * # Tracking
 *
 * `\T` tracking factor multiplies the cursor advance per glyph (not
 * the glyph width itself), exactly as AutoCAD interprets it. We
 * inherit this convention from `renderTextRun`.
 *
 * # Q15.C fallback
 *
 * If `glyphs.get(charCode) === undefined`, the renderer first tries
 * codepoint 0x3f ('?') as a substitute. If '?' is also missing the
 * cursor advances by `(above + below) / 2 × heightScale × widthFactor`
 * so the run width does not collapse to zero.
 *
 * # CLAUDE.md ПРИОРИТЕТ #1 — full fidelity
 *
 * No simplification: every stroke segment is preserved, oblique +
 * tracking + width-factor are applied per-glyph, and decorations
 * (underline / overline / strikethrough) are emitted as additional
 * line segments at the proper Y-offsets when requested. Embedded-
 * block placeholders are not rendered here — the existing PR-E4
 * placeholder rectangle path covers them at the run level.
 */

import type { TextRunInput } from "./textRunRenderer";
import type { ShxStrokeFont } from "./shxStrokeAtlas";
import { shouldSkipTextRun } from "./textScreenGate";

// ── Public types ─────────────────────────────────────────────────

/**
 * Output of `renderShxTextRun`. Vertices are flat
 * `[x1, y1, z1, x2, y2, z2, ...]` triples — one pair per line
 * segment, exactly the layout
 * `LineSegmentsGeometry.setPositions` expects.
 *
 * Z is always 0 (single-plane 2D); reserved for the rare case
 * (planned PR-L) when the renderer goes 3D and SHX text gets billboard
 * lifting.
 */
export interface ShxRunGeometry {
  /** Flat `[x,y,z, x,y,z, ...]` per-vertex buffer. */
  positions: Float32Array;
  /** RGB triple `[r,g,b]` (0..1) — one color for the whole run. */
  colorRgb: [number, number, number];
  /** Number of line segments emitted (= positions.length / 6). */
  segmentCount: number;
  /** End cursor X in text-local space — chains for multi-run layout. */
  cursorEndX: number;
  /** Whether any glyph hit the Q15.C `?` fallback. */
  fallbackUsed: boolean;
}

const EMPTY_RUN_GEOMETRY: ShxRunGeometry = {
  positions: new Float32Array(0),
  colorRgb: [1, 1, 1],
  segmentCount: 0,
  cursorEndX: 0,
  fallbackUsed: false,
};

// ── Helpers ─────────────────────────────────────────────────────

function parseColorHexRgb(colorHex: string): [number, number, number] {
  let raw = colorHex.trim();
  if (raw.startsWith("#")) raw = raw.slice(1);
  if (raw.length === 3) {
    raw = raw
      .split("")
      .map((c) => c + c)
      .join("");
  }
  if (raw.length !== 6) return [1, 1, 1];
  const r = Number.parseInt(raw.slice(0, 2), 16);
  const g = Number.parseInt(raw.slice(2, 4), 16);
  const b = Number.parseInt(raw.slice(4, 6), 16);
  if (!Number.isFinite(r) || !Number.isFinite(g) || !Number.isFinite(b)) {
    return [1, 1, 1];
  }
  return [r / 255, g / 255, b / 255];
}

// ── Public API ───────────────────────────────────────────────────

/**
 * Render one text run against an SHX stroke font.
 *
 * Walks the run text codepoint-by-codepoint, looks up each glyph's
 * pre-flattened segments in `font.glyphs`, applies per-glyph cursor
 * + scale + oblique + width-factor + tracking, and pushes the
 * transformed segment endpoints onto the output buffer.
 *
 * Returns `EMPTY_RUN_GEOMETRY` for empty text or for runs whose every
 * glyph misses both the primary lookup and the `?` fallback (and the
 * font also lacks `?`). The caller (Scene3DStage) sees
 * `segmentCount === 0` and skips mesh creation.
 */
export function renderShxTextRun(
  run: TextRunInput,
  font: ShxStrokeFont,
): ShxRunGeometry {
  if (!run.text || run.text.length === 0) return EMPTY_RUN_GEOMETRY;

  // PR-V5 — sub-pixel screen-size gate. Mirrors the SDF path's
  // `renderTextRun` short-circuit. See `textScreenGate.ts` for
  // thresholds and `textRunRenderer.ts` for the parallel logic.
  if (run.cameraZoom !== undefined) {
    const bucket = shouldSkipTextRun(run.heightWorld, run.cameraZoom);
    if (bucket === "skip") {
      return {
        ...EMPTY_RUN_GEOMETRY,
        cursorEndX: run.cursorStartX ?? 0,
      };
    }
  }

  // `above` is the SHX baseline → cap-top distance in font units.
  // `heightScale` converts font units to text-local world units so
  // the run reads at exactly the requested heightWorld.
  const above = font.above > 0 ? font.above : 1;
  const heightScale = run.heightWorld / above;
  if (!Number.isFinite(heightScale) || heightScale <= 0) {
    return EMPTY_RUN_GEOMETRY;
  }

  const colorRgb = parseColorHexRgb(run.colorHex);
  const widthFactor = run.widthFactor > 0 ? run.widthFactor : 1;
  const trackingFactor =
    run.trackingFactor !== undefined && run.trackingFactor > 0
      ? run.trackingFactor
      : 1;
  const obliqueRad = Number.isFinite(run.oblique)
    ? (run.oblique as number)
    : 0;
  const tanOblique = Math.tan(obliqueRad);
  const baselineOffset = Number.isFinite(run.baselineOffsetWorld)
    ? (run.baselineOffsetWorld as number)
    : 0;
  const wantUnderline = run.underline === true;
  const wantOverline = run.overline === true;
  const wantStrike = run.strikethrough === true;

  const fallbackGlyph = font.glyphs.get(0x3f); // '?'

  // First pass: count segments so we can pre-allocate the Float32
  // buffer at the exact size. This avoids `Array.push` thrash on long
  // runs (a single MText paragraph can easily hit 5000 segments) and
  // keeps GC pressure flat. Decoration segments are added unconditionally.
  let totalSegments = 0;
  let firstPassFallbackUsed = false;
  for (const ch of run.text) {
    const code = ch.codePointAt(0);
    if (code === undefined) continue;
    let g = font.glyphs.get(code);
    if (!g) {
      g = fallbackGlyph;
      if (g) firstPassFallbackUsed = true;
    }
    if (!g) continue;
    totalSegments += g.segments.length;
  }
  const decorationSegments =
    (wantUnderline ? 1 : 0) + (wantOverline ? 1 : 0) + (wantStrike ? 1 : 0);
  totalSegments += decorationSegments;

  if (totalSegments === 0) {
    // No visible glyphs — still advance cursor for measurement,
    // mirroring the SDF path's empty-text handling.
    let cursorAdvanceX = 0;
    for (const ch of run.text) {
      const code = ch.codePointAt(0);
      if (code === undefined) continue;
      const g = font.glyphs.get(code) ?? fallbackGlyph;
      const advance = g
        ? g.advanceX
        : (font.above + font.below) * 0.5;
      cursorAdvanceX +=
        advance * heightScale * widthFactor * trackingFactor;
    }
    return {
      ...EMPTY_RUN_GEOMETRY,
      colorRgb,
      cursorEndX: (run.cursorStartX ?? 0) + cursorAdvanceX,
      fallbackUsed: firstPassFallbackUsed,
    };
  }

  // Second pass: emit transformed segment endpoints.
  // Each segment = 6 floats (x1,y1,z1,x2,y2,z2).
  const positions = new Float32Array(totalSegments * 6);
  let writeIdx = 0;

  const cursorStartX = run.cursorStartX ?? 0;
  const cursorYBase = (run.cursorStartY ?? 0) + baselineOffset;
  let cursorX = cursorStartX;
  let fallbackUsed = false;

  // Per-glyph transform: scale by heightScale × widthFactor on X,
  // heightScale on Y, then oblique-shear, then translate by
  // (cursorX, cursorYBase).
  //
  // The transform for one segment endpoint (gx, gy) in font units is:
  //   wx = (gx × widthFactor + gy × tanOblique) × heightScale + cursorX
  //   wy = gy × heightScale + cursorYBase
  // Note: the shear is applied to the SCALED y so the slant angle
  // remains independent of widthFactor (matches AutoCAD: `\Q` is the
  // angle in screen space, not in font-unit space).

  for (const ch of run.text) {
    const code = ch.codePointAt(0);
    if (code === undefined) continue;

    let g = font.glyphs.get(code);
    if (!g) {
      g = fallbackGlyph;
      if (g) fallbackUsed = true;
    }

    if (!g) {
      // Both primary and `?` fallback missing — advance cursor by
      // half the line height so the run width doesn't collapse.
      cursorX +=
        (font.above + font.below) *
        0.5 *
        heightScale *
        widthFactor *
        trackingFactor;
      continue;
    }

    for (const seg of g.segments) {
      // Apply width-factor to the font-unit X (NOT to the shear, so
      // the oblique angle reads correctly post-scaling).
      const sx1 = seg.x1 * widthFactor;
      const sx2 = seg.x2 * widthFactor;
      // World-space X with oblique shear applied to scaled Y.
      const wx1 = (sx1 + seg.y1 * tanOblique) * heightScale + cursorX;
      const wy1 = seg.y1 * heightScale + cursorYBase;
      const wx2 = (sx2 + seg.y2 * tanOblique) * heightScale + cursorX;
      const wy2 = seg.y2 * heightScale + cursorYBase;
      positions[writeIdx++] = wx1;
      positions[writeIdx++] = wy1;
      positions[writeIdx++] = 0;
      positions[writeIdx++] = wx2;
      positions[writeIdx++] = wy2;
      positions[writeIdx++] = 0;
    }

    cursorX += g.advanceX * heightScale * widthFactor * trackingFactor;
  }

  // Decoration segments. Span the run from cursorStartX to cursorX at
  // the appropriate Y offset relative to baseline. Mirrors the SDF
  // path's per-decoration thickness rule but as a single segment (line
  // width is set on the material in Scene3DStage).
  const runWidthWorld = cursorX - cursorStartX;
  if (runWidthWorld > 0 && decorationSegments > 0) {
    if (wantUnderline) {
      const y =
        cursorYBase - Math.max(run.heightWorld * 0.05, 1) * 1.5;
      positions[writeIdx++] = cursorStartX;
      positions[writeIdx++] = y;
      positions[writeIdx++] = 0;
      positions[writeIdx++] = cursorX;
      positions[writeIdx++] = y;
      positions[writeIdx++] = 0;
    }
    if (wantOverline) {
      const y = cursorYBase + run.heightWorld * 0.8;
      positions[writeIdx++] = cursorStartX;
      positions[writeIdx++] = y;
      positions[writeIdx++] = 0;
      positions[writeIdx++] = cursorX;
      positions[writeIdx++] = y;
      positions[writeIdx++] = 0;
    }
    if (wantStrike) {
      const y = cursorYBase + run.heightWorld * 0.4;
      positions[writeIdx++] = cursorStartX;
      positions[writeIdx++] = y;
      positions[writeIdx++] = 0;
      positions[writeIdx++] = cursorX;
      positions[writeIdx++] = y;
      positions[writeIdx++] = 0;
    }
  }

  // Slice down to the actual segment count if we over-allocated (the
  // first pass over-counts when a glyph hits Q15.C double-fallback —
  // segments belonging to that glyph are zero, but we already added 0
  // to the running total so this rarely triggers; keep the slice for
  // safety).
  const actualFloats = writeIdx;
  const finalPositions =
    actualFloats === positions.length
      ? positions
      : positions.slice(0, actualFloats);

  return {
    positions: finalPositions,
    colorRgb,
    segmentCount: actualFloats / 6,
    cursorEndX: cursorX,
    fallbackUsed,
  };
}
